import React, { Fragment } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import { MainContainer, MainTitle, TitleContainer, Paragraph, ColumnContainer, Column, MainButton, ButtonsRow, SectionTitle, HeadSeo, LoadingContainer, Breadcrumbs } from '@beacharound/beacharound-ui';

import { getPlace } from '../../api/places';
import { getPlaceholderImage } from '../../helpers/images/getPlaceholderImage';
import { getGeoBreadCrumbs } from '../../helpers/getGeoBreadCrumbs';

import BeachesSlider from '../../components/Beaches/BeachesSlider';
import HotelsSlider from '../../components/Hotels/HotelsSlider';
import OffersSlider from '../../components/Offers/OffersSlider';
import GalleryComponent from '../../components/Gallery/GalleryComponent';
import BeachesRankingsHighlight from '../../components/Beaches/BeachesRankingsHighlight';
import { AdSense } from '../../components/AdSense/AdSense';

export default function PlaceDetail({ match }) {

    const { placeSlug } = match.params;

    const { data: place, isLoading } = useQuery(["places", placeSlug], () =>
        getPlace({ slug: placeSlug })
    );

    const placeName = place?.name;
    const nearString = `${place?.type === "city" ? "a" : "vicino a"}`;

    return (
        <LoadingContainer isLoading={isLoading}>
            {place && (
                <Fragment>
                    <HeadSeo
                        title={[`Prenota le spiagge a ${placeName}`]}
                        name={placeName}
                        descriptionFull={place.description}
                        // description={`Prenota le spiagge a ${place.name}. Stabilimento o Lido? Ombrellone, sdraio e lettino al miglior prezzo`}
                        description={`Prenota le spiagge a ${placeName}. Scegli tra ombrellone, sdraio o lettino, stabilimento balneare o lido al miglior prezzo online.`}
                        image={place.cover}
                        type="Place"
                        additions={{
                            geo: place.geocode?.coordinates && {
                                "@type": "GeoCoordinates",
                                "latitude": place.geocode?.coordinates[1],
                                "longitude": place.geocode?.coordinates[0]
                            }
                        }}
                    />

                    <MainContainer>
                        <TitleContainer>
                            <Breadcrumbs
                                breadcrumbs={getGeoBreadCrumbs(place?.geoElements, {
                                    others: [{
                                        label: placeName,
                                        url: match.url
                                    }]
                                })}
                                linkComponent={Link}
                            />
                            <MainTitle
                                text={`Prenota le spiagge ${nearString} <strong>${placeName}</strong>`}
                                component="h1"
                            />
                        </TitleContainer>

                        <ColumnContainer>
                            <Column>
                                <GalleryComponent
                                    cover={place.cover || getPlaceholderImage('beaches')}
                                    gallery={place.gallery}
                                    name={placeName}
                                />
                            </Column>
                        </ColumnContainer>
                    </MainContainer>

                    <BeachesSlider
                        title={`Le migliori spiagge ${nearString} ${placeName}`}
                        subtitle={`Scopri le spiagge più belle ${nearString} ${placeName} e prenota il tuo ombrellone!`}
                        place={placeSlug}
                        placeName={placeName}
                        more={match.url}
                    />

                    <MainContainer>
                        <AdSense
                            client="ca-pub-7021525903157736"
                            slot="1608548727"
                            format='auto'
                        />
                    </MainContainer>

                    <BeachesSlider
                        title={`Spiagge libere ${nearString} ${placeName}`}
                        place={placeSlug}
                        placeName={placeName}
                        publicBeaches
                        more={match.url}
                    />

                    <OffersSlider
                        title={`Offerte imperdibili ${nearString} ${placeName}`}
                        subtitle={`Scopri le promozioni e le offerte proposte dagli hotel in esclusiva per Beacharound ${nearString} ${placeName}`}
                        white
                        noMargin
                        place={placeSlug}
                        more={match.url}
                    />

                    <MainContainer>
                        <HotelsSlider
                            title={`I migliori hotel ${nearString} ${placeName}`}
                            subtitle={`Scopri le hotel più adatti a te ${nearString} ${placeName} e prenota la tua camera!`}
                            place={placeSlug}
                            more={match.url}
                        />
                    </MainContainer>

                    <BeachesRankingsHighlight
                        title={`Le selezioni di Beacharound ${nearString} ${placeName}`}
                        subtitle={"Beacharound collabora con diversi enti per ricercare sempre gli hotel di miglior qualità da offrire ai propri utenti. Scopri quali sono!"}
                        coordinates={{
                            longitude: place?.geocode?.coordinates?.[0],
                            latitude: place?.geocode?.coordinates?.[1],
                        }}
                    />

                    <MainContainer>
                        <AdSense
                            client="ca-pub-7021525903157736"
                            slot="5737449708"
                            format='auto'
                        />
                    </MainContainer>

                    <MainContainer>
                        <TitleContainer>
                            <MainTitle text="Sei il gestore di una spiaggia, un hotel o offri esperienze?" component="div" />
                            <Paragraph text="Beacharound è sempre alla ricerca di nuovi partner. Inserisci la tua attività!" component="div" />
                        </TitleContainer>
                        <ColumnContainer>
                            <Column>
                                <ButtonsRow>
                                    <MainButton
                                        border
                                        text="Inserisci la tua attività"
                                        component="a"
                                        to={process.env.REACT_APP_BUSINESS_URL}
                                        blank
                                    />
                                </ButtonsRow>
                            </Column>
                        </ColumnContainer>

                        <TitleContainer>
                            <MainTitle text="Sei il rappresentate di un comune o di un ente pubblico?" component="div" />
                            <Paragraph text="Beacharound mette a disposizione alle pubbliche amministrazioni gli strumenti necessari per promuovere e gestire le spiagge libere!" component="div" />
                        </TitleContainer>
                        <ButtonsRow>
                            <MainButton
                                border
                                text="Collabora con noi"
                                component="a"
                                to={process.env.REACT_APP_BUSINESS_URL}
                                blank
                            />
                        </ButtonsRow>

                        <ColumnContainer>
                            <Column>
                                <TitleContainer>
                                    <SectionTitle
                                        component="h2"
                                        text={`Prenota le spiagge ${nearString} ${placeName}. Stabilimento o Lido? Ombrellone, sdraio e lettino al miglior prezzo`}
                                    />
                                    {place.description &&
                                        <Paragraph text={place.description} component="div" />
                                    }
                                    <Paragraph text={`Cosa scegliere: ombrellone, sdraio o lettino? stabilimento balneare o lido? l'importante è avere un posto riservato al miglior prezzo online! Prenota subito le spiagge a ${placeName}. Basta code e stressanti attese sotto il sole ma solo pace e relax. <br/> Sdraio, lettino e ombrellone, insieme a tutto ciò che ti serve sono già in stabilimento ad attenderti. Se preferisci puoi vivere il tuo meritato riposso anche nella pace di una spiaggia libera. Non ti dovrai più preoccupare di nulla e potrai dedicarti unicamente a divertirti e rilassarti.<br/> Dai un occhio qui di seguito e scopri qual'è il luogo più adatto alle tue esigenze; a tutto il resto ci pensa Beacharound!`} />
                                </TitleContainer>
                            </Column>
                        </ColumnContainer>
                    </MainContainer>
                </Fragment>
            )}
        </LoadingContainer>
    );
}